import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { HStack, Heading, IconButton, Spinner, Stack, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { useClassFiltersStore } from '@stores/classes';
import {
  addDays,
  eachDayOfInterval,
  endOfWeek,
  format,
  isSameDay,
  isToday,
  startOfWeek,
  subDays,
  getISOWeek,
  getYear,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { useListClasses, useListLocations, useListVisits } from 'hooks/useApiQueries';
import { useCoachStudioParams } from 'hooks/useCoachStudioParams';
import { useUserContext } from 'providers/useUserContext';
import { ClassesLoading } from './Loading';
import MobileClasses from './MobileClasses';
import { ClassCard } from './components/Card';
import { ClassesFilters } from './components/Filters';
import { useIsMobile } from 'utils/functions';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// const brandName = mondayMindbody?.customization?.brandName;

function Classes() {
  const { isLoggedIn } = useUserContext();
  const { classType } = useClassFiltersStore();
  const location = useLocation();

  useEffect(() => {
    // Réinitialiser le type de cours si l'URL ne contient pas de filtre
    if (!location.pathname.includes('/cours/')) {
      useClassFiltersStore.getState().setClassType(null);
    }
  }, []);

  // Initialize date state with the current date
  const setDate = useClassFiltersStore((state) => state.setDate);
  const date = useClassFiltersStore((state) => state.date || new Date());

  const setNextWeek = useClassFiltersStore((state) => state.setNextWeek);
  const setPreviousWeek = useClassFiltersStore((state) => state.setPreviousWeek);

  // Custom function to calculate the correct week and year
  const calculateWeekAndYear = (date: Date) => {
    const week = getISOWeek(date);
    const year = getYear(date);

    // Adjust logic to handle the last week of the year
    if (week === 1 && date.getMonth() === 11) {
      return { week: getISOWeek(subDays(date, 7)), year };
    }

    return { week, year };
  };

  const [currentWeek, setCurrentWeek] = useState(calculateWeekAndYear(date).week);
  const [currentYear, setCurrentYear] = useState(calculateWeekAndYear(date).year);

  useEffect(() => {
    const { week, year } = calculateWeekAndYear(date);
    setCurrentWeek(week);
    setCurrentYear(year);
  }, [date]);

  // Update params with the current week and year
  const [params, setParams] = useState(() => ({
    week: currentWeek,
    year: currentYear,
    studioId: null as number | null,
    coachId: null as number | null,
  }));

  useEffect(() => {
    setParams((prevParams) => ({
      ...prevParams,
      week: currentWeek,
      year: currentYear,
    }));
  }, [currentWeek, currentYear]);

  const { studioId, coachId } = useClassFiltersStore();
  const { data: studios } = useListLocations();

  const { id, isStudioUrl } = useCoachStudioParams();
  const currentStudioId = isStudioUrl ? id : studioId;

  // Update studioId and coachId in params
  useEffect(() => {
    setParams((prevParams) => ({
      ...prevParams,
      studioId: studioId ? parseInt(studioId.toString(), 10) : null,
      coachId: coachId ? parseInt(coachId.toString(), 10) : null,
    }));
  }, [studioId, coachId]);

  // Trouver le studio actuel par studioId
  const currentStudio = studios?.find((studio) => {
    return parseInt(mondayMindbody.siteId.toString(), 10) === studio.SiteID && studio.Id === currentStudioId;
  });

  const TitleComponent = () => {
    if (currentStudio) {
      return (
        <Heading textAlign="center" as="h1">
          {currentStudio.Name}
        </Heading>
      );
    } else {
      return (
        <>
          <Heading as="h1" textAlign="center">
            Planning
          </Heading>
          <Text as="h2" fontSize="lg" color="gray.600" textAlign="center">
            Tous studios
          </Text>
        </>
      );
    }
  };

  // Fonctions pour naviguer d'un jour à l'autre
  const handleNextDay = () => {
    const nextDay = addDays(date, 1);
    setDate(nextDay);
  };

  const handlePreviousDay = () => {
    const previousDay = subDays(date, 1);
    setDate(previousDay);
  };

  const { data: classesData, isLoading: isClassesLoading, isError } = useListClasses(params);

  const filteredClassesData = classesData?.filter((classData) => {
    if (classType === 'yoga') {
      return classData.ClassDescription.Name.toLowerCase().includes('yoga'); // Sinon utiliser .Category === 'Yoga'
    } else if (classType === 'pilates') {
      return classData.ClassDescription.Name.toLowerCase().includes('pilates');
    } else if (classType === 'reformer') {
      return classData.ClassDescription.Name.toLowerCase().includes('reformer');
    }
    return classData.Active; // Aucun filtre appliqué, retourne tous les cours actifs
  });

  const { isLoading: isVisitsLoading } = useListVisits() || {};

  const startOfWeekDate = startOfWeek(date, { weekStartsOn: 1 });
  const endOfWeekDate = endOfWeek(date, { weekStartsOn: 1 });
  const days = eachDayOfInterval({
    start: startOfWeekDate,
    end: endOfWeekDate,
  });

  const isLoading = isClassesLoading || (isLoggedIn && isVisitsLoading);

  const isMobile = useIsMobile();

  // Logique pour afficher la vue mobile
  if (isMobile) {
    return (
      <>
        <Stack alignItems="center">
          <TitleComponent />
          <ClassesFilters />
          {isClassesLoading ? (
            <Spinner mt={2} size="xl" />
          ) : (
            <MobileClasses
              date={date}
              classesData={filteredClassesData || []}
              onNextDay={handleNextDay}
              onPreviousDay={handlePreviousDay}
            />
          )}
        </Stack>
      </>
    );
  }

  return (
    <Stack alignItems="center">
      <Heading as="div" className="mainAppTitle" display={mondayMindbody.coach ? 'none' : 'block'}>
        <TitleComponent />
      </Heading>
      <ClassesFilters />
      <Table marginTop="-1px" variant="unstyled" layout="fixed" className="planningTable">
        <Thead className="planningTableHead">
          <Tr className="calendarDays">
            {days.map((day, index) => (
              <Th key={index} className="calendarDay" backgroundColor={isToday(day) ? '#D9D9D9' : '#F5F5F5'}>
                <Stack spacing={1} flexGrow={1} alignItems="center" textAlign="center">
                  <Text
                    mb="1"
                    letterSpacing={0}
                    textTransform="capitalize"
                    fontSize="md"
                    color="black"
                    fontWeight="semibold"
                  >
                    {format(day, 'EEEE', { locale: fr })} {/* Utilise 'EEEE' pour le jour complet en français */}
                  </Text>
                  <Text letterSpacing={0} fontSize="xs" fontWeight="semibold">
                    {format(day, 'dd/MM', { locale: fr })} {/* Formate la date en '29/01' */}
                  </Text>
                </Stack>
              </Th>
            ))}
          </Tr>
        </Thead>

        <Tbody className="planningTableBody">
          <Tr>
            {filteredClassesData?.length === 0 ? (
              <Td colSpan={days.length}>
                <Stack spacing={0} h="full">
                  <Text align="center" className="noClasses">
                    Aucune séance trouvée
                  </Text>
                </Stack>
              </Td>
            ) : isError ? (
              <Td colSpan={days.length}>
                <Text fontSize="3xl" fontWeight="medium" textAlign="center">
                  Une erreur est survenue
                </Text>
              </Td>
            ) : isLoading ? (
              days.map((_, index) => (
                <Td key={index} p={2} verticalAlign="top">
                  <ClassesLoading />
                </Td>
              ))
            ) : (
              days.map((day, index) => {
                const classes = ((Array.isArray(filteredClassesData) && filteredClassesData) || [])?.filter((current) =>
                  isSameDay(day, new Date(current.StartDateTime)),
                );
                return (
                  <Td key={index} p={0} verticalAlign="top" borderLeft="1px solid #D9D9D9">
                    <Stack spacing={0} h="full">
                      {classes.map((mindbodyClass) => (
                        <ClassCard key={mindbodyClass.Id} mindbodyClass={mindbodyClass} />
                      ))}
                    </Stack>
                  </Td>
                );
              })
            )}
          </Tr>
        </Tbody>
      </Table>
      <HStack spacing={2} alignItems="flex-end" justifyContent="space-between" w="full" backgroundColor="#F5F5F5">
        <Text cursor="pointer" color="black" onClick={setPreviousWeek} className="previousButton">
          <IconButton
            backgroundColor="transparent"
            color="black"
            aria-label="Previous"
            className="previousIcon"
            icon={<ChevronLeftIcon backgroundColor="transparent" color="black" />}
          />
          semaine précédente
        </Text>
        <Text cursor="pointer" color="black" onClick={setNextWeek} className="nextButton">
          semaine suivante{' '}
          <IconButton
            backgroundColor="transparent"
            color="black"
            aria-label="Next"
            className="nextIcon"
            icon={<ChevronRightIcon backgroundColor="transparent" color="black" />}
          />
        </Text>
      </HStack>
      <HStack align="center" marginTop="0">
        <Text color="#D9D9D9" marginTop="0" className="bottomNote">
          * cours accessibles au packs étudiants
        </Text>
      </HStack>
    </Stack>
  );
}

export { Classes };
